import zod, { z } from 'zod';
import { IvsDeviceAvailableEvents } from './IvsService/schemas';
import { TemperatureDetectorDeviceAvailableEvents } from './TemperatureDetectorService/schemas';
import { MANDATORY_FIELD } from '../../config/zodConfig';
import { RecognitionCameraAvailableEvents } from './Recognition/Cameras/Intelbras/RecognitionCameraService/schemas';
import { FacialControllerAvailableEvents } from './Recognition/Controllers/schemas';
import { IRecordingPeriodProps } from './types';

export const LprDeviceAvailableEvents = zod.enum([
  'TRAFFIC_RUNREDLIGHT',
  'TRAFFICJUNCTION',
  'TRAFFIC_OVERSPEED',
  'TRAFFIC_UNDERSPEED',
  'TRAFFIC_MANUALSNAP',
  'TRAFFIC_PARKINGSPACEPARKING',
  'TRAFFIC_PARKINGSPACENOPARKING',
  'TRAFFIC_OVERLINE',
  'TRAFFIC_RETROGRADE',
  'TRAFFIC_PARKING',
  'TRAFFIC_WRONGROUTE',
  'TRAFFIC_CROSSLANE',
  'TRAFFIC_OVERYELLOWLINE',
  'TRAFFIC_YELLOWPLATEINLANE',
  'TRAFFIC_PEDESTRAINPRIORITY',
  'TRAFFIC_VEHICLEINROUTE',
  'TRAFFIC_VEHICLEINBUSROUTE',
  'TRAFFIC_BACKING',
  'TRAFFIC_WITHOUT_SAFEBELT',
]);

const options = zod.enum([
  ...LprDeviceAvailableEvents.options,
  ...IvsDeviceAvailableEvents.options,
  ...TemperatureDetectorDeviceAvailableEvents.options,
  ...FacialControllerAvailableEvents.options,
  ...RecognitionCameraAvailableEvents.options,
]);

export const DeviceFormSchema = zod
  .object({
    id: zod.number().optional(),
    name: zod.string().min(1, MANDATORY_FIELD),
    username: zod.string().min(1, MANDATORY_FIELD),
    ip: zod.string().optional(),
    httpPort: zod.string().optional(),
    tcpPort: zod.string().optional(),
    rtspPort: zod.string().optional(),
    password: zod.string().min(1, MANDATORY_FIELD),
    brand: zod.enum(['INTELBRAS', 'HIKVISION', 'CONTROLID', 'TOPDATA']),
    model: zod.string(),
    serialNumber: zod.string().optional(),
    channel: zod.number().optional(),
    channels: zod.array(zod.number()).optional(),
    connection: zod.number(),
    availableEvents: zod.array(options).optional(),
    numberOfChannels: zod.number().optional(),
    numberOfAlarmEntries: zod.number().optional(),
    uc: zod.string().optional().nullable(),
  })
  .superRefine((arg, ctx) => {
    if (arg.connection === 0 && (!arg.ip || arg.ip.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['ip'],
        message: MANDATORY_FIELD,
      });
    }

    if (arg.connection === 0 && (!arg.httpPort || arg.httpPort.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['httpPort'],
        message: MANDATORY_FIELD,
      });
    }

    if (arg.connection === 0 && (!arg.tcpPort || arg.tcpPort.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tcpPort'],
        message: MANDATORY_FIELD,
      });
    }

    if (arg.brand === 'TOPDATA' && !arg.serialNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['serialNumber'],
        message: MANDATORY_FIELD,
      });
    }
  });

export const DeviceSimpleFormSchema = zod.object({
  id: zod.number().optional(),
  name: zod.string().nonempty(),
  brand: zod.string().optional(),
  model: zod.string().optional(),
  isOnline: zod.boolean(),
  description: zod.string().optional(),
});

export const DeviceNetworkConfigFormSchema = zod.object({
  hostname: zod.string().min(1, MANDATORY_FIELD),
  ip: zod.string().min(1, MANDATORY_FIELD),
  subnetMask: zod.string().min(1, MANDATORY_FIELD),
  defaultGateway: zod.string().min(1, MANDATORY_FIELD),
  dnsServerPreference: zod.string().min(1, MANDATORY_FIELD),
  isDhcp: zod.boolean(),
});

export const DeviceRecoverEventConfigFormSchema = zod.object({
  intervalHours: zod.number().min(1, MANDATORY_FIELD),
  isEnable: zod.boolean(),
  recoverEventType: zod.string().min(1, MANDATORY_FIELD),
});

export const DeviceNtpConfigFormSchema = zod.object({
  isEnabled: zod.boolean(),
  port: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  address: zod.string().min(1, MANDATORY_FIELD),
  timeZone: zod.string().min(1, MANDATORY_FIELD),
  timeZoneDesc: zod.string(),
  updatePeriodMinutes: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
});

export const DeviceVideoConfigFormSchema = zod.object({
  encodingType: zod.string().min(1, MANDATORY_FIELD),
  frame: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  frameInterval: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  bitRate: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  heightScreen: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  widthScreen: zod.number({
    required_error: MANDATORY_FIELD,
    invalid_type_error: MANDATORY_FIELD,
  }),
  bitRateType: zod.string().min(1, MANDATORY_FIELD),
});

export const RecordingPeriodFormSchema = zod.object({
  initialMinutes: zod.number(),
  initialSeconds: zod.number(),
  initialHours: zod.number(),
  endMinutes: zod.number(),
  endSeconds: zod.number(),
  endHours: zod.number(),
  isRecordRegular: zod.boolean(),
  isRecordEvent: zod.boolean(),
});

export const DeviceRecordingScheduleConfigFormSchema = zod.object({
  sunday: RecordingPeriodFormSchema,
  monday: RecordingPeriodFormSchema,
  tuesday: RecordingPeriodFormSchema,
  wednesday: RecordingPeriodFormSchema,
  thursday: RecordingPeriodFormSchema,
  friday: RecordingPeriodFormSchema,
  saturday: RecordingPeriodFormSchema,
  preRecordTimeSeconds: zod.number(),
  isEnabledRecord: zod.boolean(),
  maxRecordTimeMinutes: zod.number(),
});
